/**
 * @file LegalDocumentCardView.tsx
 * @description Открытая карточка документа (phase19.16, SB139–SB144):
 * шапка с пилюлей состояния и кнопками, предупреждения, «Где используется»,
 * поля и секция вида (`LegalDocBody`), окна снятия и удаления.
 *
 * Один источник правды — карточка из ответа API: сохранение и публикация
 * сбрасывают форму на неё (`useLegalDocActions`); загрузка файла и правка
 * состава группы кладут карточку, не трогая несохранённые поля.
 */

import { RotateCcw, Trash2, Undo2, EyeOff } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";

import { CabinetPageHeader } from "../ui/CabinetPageHeader";
import { ListError, ListSkeleton } from "../ui/ListStates";
import { NoticeStrip } from "../ui/NoticeStrip";
import { useNotify } from "../ui/NotifyProvider";
import type { PageAction } from "../ui/page-actions";
import { StatusPill } from "../ui/StatusPill";
import { DeleteLegalDialog } from "./DeleteLegalDialog";
import { LegalDocBody } from "./LegalDocBody";
import { LegalSaveButtons, LEGAL_PUBLISH_REASON_ID } from "./LegalSaveButtons";
import { UnpublishLegalDialog } from "./UnpublishLegalDialog";
import { useLegalDocActions } from "../../hooks/legal/useLegalDocActions";
import { useLegalDocument } from "../../hooks/legal/useLegalDocument";
import { useLegalDraftForm, validateLegalDraft } from "../../hooks/legal/useLegalDraftForm";
import { useLegalFileUpload } from "../../hooks/legal/useLegalFileUpload";
import { useLegalRevisions } from "../../hooks/legal/useLegalRevisions";
import { deleteLegalDraftFile } from "../../lib/api-legal";
import { LEGAL_NOTHING_TO_PUBLISH_TEXT, legalCardButtons, legalPublishBlock } from "../../lib/legal/legal-card-actions";
import { legalGroupVisibleCount } from "../../lib/legal/legal-certificates";
import { legalStatusMeta, legalWarningText } from "../../lib/legal/legal-doc-view";
import { usageSummary } from "../../lib/legal/legal-usage-lines";

export function LegalDocumentCardView({ id }: { id: string }) {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const listHref = `/shop/${slug}/settings/legal-documents`;
  const back = { to: listHref, label: "Юридические документы" };
  const { doc, setDoc, loading, error, reload } = useLegalDocument(id);
  const kind = doc?.kind ?? "TEXT";
  const form = useLegalDraftForm(doc, kind);
  const actions = useLegalDocActions(doc, setDoc, form, reload);
  const revisions = useLegalRevisions(doc && doc.kind !== "GROUP" ? doc.id : null, doc?.live?.number ?? null);
  const upload = useLegalFileUpload();
  const [dialog, setDialog] = useState<"unpublish" | "delete" | null>(null);

  if (!doc) {
    return (
      <div className="flex flex-col gap-4">
        <CabinetPageHeader title="Документ" back={back} />
        {loading ? <ListSkeleton rows={4} compact /> : <ListError message={error ?? "Документ не найден"} onRetry={() => void reload()} />}
      </div>
    );
  }

  const errors = validateLegalDraft(form.values, doc.kind, false);
  const invalid = Object.keys(errors).length > 0;
  // «Сразу скачивать / в браузере» сохраняется без редакции — публиковать после него нечего.
  const blockReason = legalPublishBlock(doc, doc.kind, form.values, form.revisionDirty);
  const buttons = legalCardButtons(doc, form.revisionDirty);
  const usage = usageSummary(doc);
  const busy = actions.busy === "discard" ? "save" : actions.busy;

  async function pickFile(file: File) {
    const next = await upload.start(id, file);
    if (next) {
      setDoc(next);
      notify.success("Файл загружен — опубликуйте, чтобы он появился на сайте");
    }
  }

  async function revertFile() {
    try {
      setDoc(await deleteLegalDraftFile(id, doc?.version));
    } catch (err) {
      actions.fail(err, "Не удалось вернуть файл редакции");
    }
  }

  const menu: PageAction[] = [
    ...(doc.hasDraftChanges && doc.live ? [{ key: "discard", label: "Отменить правки", icon: Undo2, run: () => void actions.discard() }] : []),
    ...(doc.isPublished ? [{ key: "unpublish", label: "Снять с публикации…", icon: EyeOff, run: () => setDialog("unpublish") }] : []),
    { key: "reload", label: "Обновить", icon: RotateCcw, run: () => void reload().then((fresh) => fresh && form.reset(fresh)) },
    { key: "delete", label: "Удалить…", icon: Trash2, danger: true, run: () => setDialog("delete") },
  ];

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title={doc.title || "Без названия"}
        back={back}
        status={<StatusPill meta={legalStatusMeta({ ...doc, visibleMembers: legalGroupVisibleCount(doc) })} />}
        action={
          <LegalSaveButtons
            buttons={buttons}
            blockReason={blockReason}
            nothingToSave={!form.dirty}
            invalid={invalid}
            busy={busy}
            onSave={() => void actions.saveDraft()}
            onPublish={() => void actions.publish()}
          />
        }
        menu={menu}
      />

      {doc.warnings.map((w) => (
        <NoticeStrip key={w.code} tone="warning">
          {legalWarningText(w)}
        </NoticeStrip>
      ))}
      {blockReason && buttons.main.run === "publish" ? (
        <p id={LEGAL_PUBLISH_REASON_ID} className="type-caption text-on-surface-variant">
          {blockReason === LEGAL_NOTHING_TO_PUBLISH_TEXT ? blockReason : `Чтобы опубликовать: ${blockReason}`}
        </p>
      ) : null}
      <p className="type-caption text-on-surface-variant">
        {usage.length > 0 ? `Где используется: ${usage.join("; ")}.` : "Ссылок на документ пока нет — добавьте его в меню подвала или в блок «Ссылки» подвала."}
      </p>

      <LegalDocBody
        kind={doc.kind}
        doc={doc}
        values={form.values}
        onChange={form.set}
        errors={errors}
        upload={upload}
        onPickFile={(file) => void pickFile(file)}
        onRevertFile={() => void revertFile()}
        revisions={revisions}
        onDoc={setDoc}
        fail={actions.fail}
        disabled={actions.busy !== null}
      />

      <UnpublishLegalDialog
        doc={dialog === "unpublish" ? doc : null}
        onClose={() => setDialog(null)}
        onDone={(result) => {
          setDialog(null);
          actions.apply(result.document);
          notify.success(doc.kind === "GROUP" ? "Группа снята с публикации" : "Документ снят с публикации — вернуть можно кнопкой «Показать снова»");
          for (const w of result.warnings) notify.warning(legalWarningText(w));
        }}
      />
      <DeleteLegalDialog
        doc={dialog === "delete" ? doc : null}
        onClose={() => setDialog(null)}
        onDeleted={() => {
          notify.success(doc.kind === "GROUP" ? "Группа удалена" : "Черновик удалён");
          navigate(listHref);
        }}
        onUnpublish={() => setDialog("unpublish")}
      />
    </div>
  );
}
